'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations, LanguageCode } from '@/lib/i18n/translations';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'feed-lms-language';

interface LanguageContextValue {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<LanguageCode>('en');
  const { profile, getUserProfile } = useAuth();

  // Read saved preference after mount only (avoids SSR/client text mismatch).
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as LanguageCode | null;
    if (stored && translations[stored]) {
      setLanguageState(stored);
    }
  }, []);

  // Keep <html lang> in step so fonts, spell-check and screen readers follow the UI language.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Once logged in, the account's saved language preference wins.
  useEffect(() => {
    if (profile?.language_pref && translations[profile.language_pref as LanguageCode]) {
      setLanguageState(profile.language_pref as LanguageCode);
      localStorage.setItem(STORAGE_KEY, profile.language_pref);
    }
  }, [profile?.language_pref]);

  const setLanguage = useCallback((code: LanguageCode) => {
    setLanguageState(code);
    localStorage.setItem(STORAGE_KEY, code);
    if (profile) {
      import('@/lib/api/auth').then(({ authApi }) => {
        authApi.updateProfile({ language_pref: code }).then(() => getUserProfile()).catch(() => {});
      });
    }
  }, [profile, getUserProfile]);

  const t = useCallback((key: string) => {
    return translations[language]?.[key] ?? translations.en[key] ?? key;
  }, [language]);

  return (
    <LanguageContext.Provider data-component-id="src/contexts/LanguageContext.tsx:63:4" data-component-path="src/contexts/LanguageContext.tsx" data-component-line="63" data-component-end-line="65" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
